import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useDebounce } from './useDebounce';

const SearchInput = ({
  searchValue = '',
  setSearchValue,
  placeholder,
}: {
  searchValue?: string;
  setSearchValue: (term: string) => void;
  placeholder: string;
}) => {
  return (
    <input
      type="text"
      placeholder={placeholder}
      className="w-full border-0 bg-transparent focus:ring-0"
      data-testid="search-input"
      value={searchValue}
      onChange={(event) => setSearchValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          setSearchValue(searchValue);
        }
      }}
    />
  );
};

export const SearchFilter = ({
  placeholder,
  setValue,
  id,
  value,
  clearCount,
  justifyCenter = true,
  showClear,
  wrapperClassName,
  dataTestId,
}: {
  placeholder: string;
  setValue: (key: string, value: string | undefined) => void;
  id: string;
  value?: string;
  /** Changes when the filter is emptied from outside, e.g. by Clear all. */
  clearCount?: number;
  justifyCenter?: boolean;
  showClear?: boolean;
  wrapperClassName?: string;
  dataTestId?: string;
}) => {
  const [query, setQuery] = useState(value);
  const debouncedSearchValue = useDebounce(query);
  useEffect(() => {
    setQuery(value);
  }, [value]);
  // Clear all drops unwritten text too, even when the URL had no search.
  const appliedClearCount = useRef(clearCount);
  useEffect(() => {
    if (appliedClearCount.current !== clearCount) {
      appliedClearCount.current = clearCount;
      setQuery('');
    }
  }, [clearCount]);
  // Writes once the typing has settled. Keying on the typed text too means
  // retyping the last debounced value after a reset is still written.
  useEffect(() => {
    if (
      debouncedSearchValue !== undefined &&
      debouncedSearchValue === query &&
      debouncedSearchValue.trim() !== value
    ) {
      setValue(id, debouncedSearchValue.trim());
    }
  }, [debouncedSearchValue, query]);

  return (
    <div
      data-testid={dataTestId}
      className={`relative inline-block h-[40px] flex-grow-0 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 antialiased ${wrapperClassName}`}
    >
      <div
        className={`flex h-full w-full bg-transparent text-gray-600 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500 ${
          justifyCenter && 'justify-center'
        } items-center font-normal focus:border-gray-300 dark:focus:border-gray-600 focus:ring-1 focus:ring-gray-500 dark:focus:ring-gray-400 focus:outline-none`}
      >
        <MagnifyingGlassIcon className="ml-2 h-4 w-4" />
        <div className="flex-1">
          <SearchInput
            placeholder={placeholder}
            searchValue={query}
            setSearchValue={setQuery}
          />
        </div>

        {showClear && query && (
          <button
            className="flex items-center justify-end self-stretch justify-self-stretch rounded-r-md px-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
            onClick={() => {
              setValue(id, '');
            }}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
